import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Spinner, alert } from 'equasion-lib';
import ApiError from '@/lib/shared-coms/ApiError';
import { useRootStore } from '@/lib/providers/RootProvider';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { refusalReason } from '@/lib/scanning/productUpcLink';
import { salesOrderVerifyPath } from '@/lib/pickVerifyShip';
import { useCombineStep } from '@/lib/shared-coms/CombineShipment';
import SalesOrderPick from './SalesOrderPick';
import {
  PICK_VERIFY_NOT_PICKING_MESSAGE,
  classifyPickRefusal,
  pickClaimView,
  pickCompleteText,
  pickListGuard,
  pickShouldAutoStart,
  type PickActionResult,
  type PickCompletion,
  type PickConfirmLineArgs,
  type PickList,
  type PickScanUnitArgs,
  type PickQuarantine,
  type PickQuarantineDamagedArgs,
  type PickQuarantineMissingArgs,
  type PickQuarantineResult
} from './pickLineSteps';

// One selection for the list, shared by the query and every mutation that answers with it — each
// Pick action returns the WHOLE list (walk order, allocations, counts, the complete gate), and the
// screen replaces its state with that answer rather than patching it. Plain strings rather than a
// fragment so scripts/gql-frontend-validate.cjs resolves them against the schema too.
const PICK_ZONE_FIELDS = `
  id
  name
  zoneType
  pickSequence
  locationId
`;

const PICK_SCANNED_UNIT_FIELDS = `
  serialNumber
  gtin
  lot
  expiration
  zone {
    id
    name
  }
  manualEntry
  scannedAt
  scannedByName
`;

const PICK_QUARANTINE_FIELDS = `
  reason
  zone {
    ${PICK_ZONE_FIELDS}
  }
  quarantineZone {
    ${PICK_ZONE_FIELDS}
  }
  quantity
  serialNumber
  lot
  expiration
  quarantinedAt
`;

const PICK_LIST_FIELDS = `
  salesOrderId
  vendorSalesOrderNumber
  vendorStatus
  clientName
  workflow {
    workflow
    stage
    started
    pickerUserId
    pickerName
    pickerClaimedAt
    pickerLastActiveAt
    pickerClaimStale
  }
  claimedByMe
  pathConfigured
  anchorZone {
    ${PICK_ZONE_FIELDS}
  }
  anchorSource
  anchorOnPath
  direction
  stops {
    key
    orderItemId
    rank
    zone {
      ${PICK_ZONE_FIELDS}
    }
    quantity
    lot
    expiration
    available
    rows {
      zone {
        ${PICK_ZONE_FIELDS}
      }
      lot
      expiration
      quantity
    }
    part
    partCount
  }
  lines {
    orderItemId
    rank
    product {
      id
      title
      fdaProduct {
        id
        name
        elevenDigitNdc
        deaSchedule
      }
      upcProduct {
        id
        upc
      }
    }
    productName
    ndc
    upc
    deaSchedule
    kind
    quantity
    isShortDated
    isChargeback
    controlled
    controlledLocked
    unitScanRequired
    scannedQuantity
    scannedUnits {
      ${PICK_SCANNED_UNIT_FIELDS}
    }
    expectedZone {
      ${PICK_ZONE_FIELDS}
    }
    expectedLot
    expectedExpiration
    expectedAvailable
    additionalStops {
      zone {
        ${PICK_ZONE_FIELDS}
      }
      lot
      expiration
      quantity
    }
    grabStops {
      zone {
        ${PICK_ZONE_FIELDS}
      }
      lot
      expiration
      quantity
    }
    noStock
    eligibleZones {
      ${PICK_ZONE_FIELDS}
    }
    skippedZones {
      ${PICK_ZONE_FIELDS}
    }
    quarantines {
      ${PICK_QUARANTINE_FIELDS}
    }
    status
    foundQuantity
    shortReason
    pickedQuantity
    confirmedStops {
      zone {
        ${PICK_ZONE_FIELDS}
      }
      quantity
      lot
      expiration
      short
      rows {
        zone {
          ${PICK_ZONE_FIELDS}
        }
        lot
        expiration
        quantity
      }
      confirmedAt
      confirmedByName
      scannedQuantity
      units {
        ${PICK_SCANNED_UNIT_FIELDS}
      }
    }
    confirmedZone {
      ${PICK_ZONE_FIELDS}
    }
    confirmedAt
    confirmedByName
  }
  pickedLineCount
  shortLineCount
  openLineCount
  controlledLockedLineCount
  canComplete
  completeBlockedReason
  usesTotes
  toteName
`;

export const SALES_ORDER_PICK_LIST = gql`
  query salesOrderPickList($companyId: ID!, $salesOrderId: ID!, $startZoneId: ID) {
    salesOrderPickList(
      companyId: $companyId
      salesOrderId: $salesOrderId
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
    # The per-page company read every blocking flag comes from (never the root store, which is
    # fetched once per app load). \`id\` is REQUIRED so Apollo normalizes this to Company:<id>. The
    # list's own \`usesTotes\` / \`workflow\` are what this screen gates on — they are read on the
    # primary with the order, and are what completeSalesOrderPick enforces; this read keeps the
    # shared Company:<id> entry fresh for the screens that route on it.
    company(id: $companyId) {
      id
      usesTotes
      fulfillmentWorkflow
    }
  }
`;

export const SALES_ORDER_PICK_START = gql`
  mutation salesOrderPickStart(
    $companyId: ID!
    $salesOrderId: ID!
    $takeOver: Boolean
    $startZoneId: ID
  ) {
    salesOrderPickStart(
      companyId: $companyId
      salesOrderId: $salesOrderId
      takeOver: $takeOver
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_RELEASE = gql`
  mutation salesOrderPickRelease($companyId: ID!, $salesOrderId: ID!) {
    salesOrderPickRelease(companyId: $companyId, salesOrderId: $salesOrderId)
  }
`;

export const SALES_ORDER_PICK_CONFIRM_LINE = gql`
  mutation salesOrderPickConfirmLine(
    $companyId: ID!
    $salesOrderId: ID!
    $input: SalesOrderPickConfirmInput!
    $startZoneId: ID
  ) {
    salesOrderPickConfirmLine(
      companyId: $companyId
      salesOrderId: $salesOrderId
      input: $input
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_MARK_SHORT = gql`
  mutation salesOrderPickMarkShort(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $foundQuantity: Int!
    $startZoneId: ID
  ) {
    salesOrderPickMarkShort(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      foundQuantity: $foundQuantity
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

// The short of a unit-scan (controlled) line: its units scanned and the reason the server requires.
// Its own document, so every other short is sent exactly as before.
export const SALES_ORDER_PICK_MARK_SHORT_WITH_REASON = gql`
  mutation salesOrderPickMarkShortWithReason(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $foundQuantity: Int!
    $reason: String!
    $startZoneId: ID
  ) {
    salesOrderPickMarkShort(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      foundQuantity: $foundQuantity
      reason: $reason
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

// A unit-scan (controlled) line: one more unit at a zone already confirmed for it, and taking one
// back off (that serial, or — none — the last scanned). Both answer with the whole list.
export const SALES_ORDER_PICK_SCAN_UNIT = gql`
  mutation salesOrderPickScanUnit(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $zoneId: ID
    $unit: PickVerifyUnitInput!
    $startZoneId: ID
  ) {
    salesOrderPickScanUnit(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      zoneId: $zoneId
      unit: $unit
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_REMOVE_UNIT = gql`
  mutation salesOrderPickRemoveUnit(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $serialNumber: String
    $startZoneId: ID
  ) {
    salesOrderPickRemoveUnit(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      serialNumber: $serialNumber
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_MARK_STOP_SHORT = gql`
  mutation salesOrderPickMarkStopShort(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $zoneId: ID!
    $foundQuantity: Int!
    $startZoneId: ID
  ) {
    salesOrderPickMarkStopShort(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      zoneId: $zoneId
      foundQuantity: $foundQuantity
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_RESET_LINE = gql`
  mutation salesOrderPickResetLine(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $zoneId: ID
    $startZoneId: ID
  ) {
    salesOrderPickResetLine(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      zoneId: $zoneId
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_SKIP_ZONE = gql`
  mutation salesOrderPickSkipZone(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $zoneId: ID!
    $startZoneId: ID
  ) {
    salesOrderPickSkipZone(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      zoneId: $zoneId
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_RESTORE_ZONES = gql`
  mutation salesOrderPickRestoreZones(
    $companyId: ID!
    $salesOrderId: ID!
    $orderItemId: ID!
    $zoneId: ID
    $startZoneId: ID
  ) {
    salesOrderPickRestoreZones(
      companyId: $companyId
      salesOrderId: $salesOrderId
      orderItemId: $orderItemId
      zoneId: $zoneId
      startZoneId: $startZoneId
    ) {
      ${PICK_LIST_FIELDS}
    }
  }
`;

export const SALES_ORDER_PICK_QUARANTINE_MISSING = gql`
  mutation salesOrderPickQuarantineMissing(
    $companyId: ID!
    $salesOrderId: ID!
    $input: SalesOrderPickQuarantineMissingInput!
    $startZoneId: ID
  ) {
    salesOrderPickQuarantineMissing(
      companyId: $companyId
      salesOrderId: $salesOrderId
      input: $input
      startZoneId: $startZoneId
    ) {
      ${PICK_QUARANTINE_FIELDS}
      list {
        ${PICK_LIST_FIELDS}
      }
    }
  }
`;

export const SALES_ORDER_PICK_QUARANTINE_DAMAGED = gql`
  mutation salesOrderPickQuarantineDamaged(
    $companyId: ID!
    $salesOrderId: ID!
    $input: SalesOrderPickQuarantineDamagedInput!
    $startZoneId: ID
  ) {
    salesOrderPickQuarantineDamaged(
      companyId: $companyId
      salesOrderId: $salesOrderId
      input: $input
      startZoneId: $startZoneId
    ) {
      ${PICK_QUARANTINE_FIELDS}
      list {
        ${PICK_LIST_FIELDS}
      }
    }
  }
`;

export const SALES_ORDER_PICK_COMPLETE = gql`
  mutation salesOrderPickComplete($companyId: ID!, $salesOrderId: ID!) {
    salesOrderPickComplete(companyId: $companyId, salesOrderId: $salesOrderId) {
      verifyOrder {
        id
        vendorSalesOrderNumber
      }
      shortOrder {
        id
        vendorSalesOrderNumber
      }
    }
  }
`;

/** While another picker's LIVE claim blocks this screen, re-read so Take over enables once it is stale. */
export const PICK_CLAIM_POLL_MS = 30000;

export type PickBusy =
  | 'start'
  | 'short'
  | 'reset'
  | 'restore'
  | 'complete'
  | 'release'
  | null;

const ACTION_FALLBACK = 'Could not update the pick. Please try again.';

function SalesOrderPickData() {
  const { id: salesOrderId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = useRootStore((state) => state.selectedCompany.id) as string;
  const client = useApolloClient();
  const [startMutation] = useMutation(SALES_ORDER_PICK_START);
  const [releaseMutation] = useMutation(SALES_ORDER_PICK_RELEASE);
  const [confirmMutation] = useMutation(SALES_ORDER_PICK_CONFIRM_LINE);
  const [markShortMutation] = useMutation(SALES_ORDER_PICK_MARK_SHORT);
  const [markShortWithReasonMutation] = useMutation(SALES_ORDER_PICK_MARK_SHORT_WITH_REASON);
  const [markStopShortMutation] = useMutation(SALES_ORDER_PICK_MARK_STOP_SHORT);
  const [scanUnitMutation] = useMutation(SALES_ORDER_PICK_SCAN_UNIT);
  const [removeUnitMutation] = useMutation(SALES_ORDER_PICK_REMOVE_UNIT);
  const [resetMutation] = useMutation(SALES_ORDER_PICK_RESET_LINE);
  const [skipZoneMutation] = useMutation(SALES_ORDER_PICK_SKIP_ZONE);
  const [restoreZonesMutation] = useMutation(SALES_ORDER_PICK_RESTORE_ZONES);
  const [quarantineMissingMutation] = useMutation(SALES_ORDER_PICK_QUARANTINE_MISSING);
  const [quarantineDamagedMutation] = useMutation(SALES_ORDER_PICK_QUARANTINE_DAMAGED);
  const [completeMutation] = useMutation(SALES_ORDER_PICK_COMPLETE);

  // "Start from a zone" lives in the URL, as on the standard picking screen, so it survives Add
  // Tote and back. Every read and write of the list passes it: the walk order is recomputed from it.
  const startZoneId = searchParams.get('startZoneId') || null;
  const setStartZoneId = useCallback(
    (zoneId: string | null) =>
      setSearchParams(zoneId ? { startZoneId: zoneId } : {}, { replace: true }),
    [setSearchParams]
  );

  const [list, setList] = useState<PickList | null>(null);
  const [company, setCompany] = useState<any>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [busy, setBusy] = useState<PickBusy>(null);
  const [startError, setStartError] = useState<string | null>(null);
  // Set by Complete Pick: the "Pick complete" panel, until the picker picks Continue or Done.
  const [completed, setCompleted] = useState<PickCompletion | null>(null);

  // Newest-request-wins. The list is replaced wholesale by whichever read or write answers, so an
  // answer to a request that STARTED before the one already applied (a re-anchor read overtaken by
  // a confirm, a poll overtaken by a mark-short) is dropped rather than rolling the screen back.
  const requestSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);
  const applyList = useCallback((seq: number, next: PickList | null | undefined) => {
    if (!next || seq < appliedSeqRef.current) return;
    appliedSeqRef.current = seq;
    setList(next);
  }, []);

  // Set once this screen is on its way out (lost order, Pick complete, released) so nothing else —
  // a late answer, the guard effect, a second alert — acts on an order the picker has left. After
  // Pick complete the screen stays mounted under its panel until the picker chooses where to go.
  const leavingRef = useRef(false);
  // The claim is taken automatically once per "free/mine" sighting; a refusal or another picker's
  // claim re-arms it, a failed start does not (the picker gets the reason and a Try again).
  const autoStartArmedRef = useRef(true);

  const leave = useCallback(
    (title: string, message: string) => {
      if (leavingRef.current) return;
      leavingRef.current = true;
      alert(title, message, 8, 'error');
      navigate('/sales-orders', { replace: true });
    },
    [navigate]
  );

  const loadList = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    try {
      const { data } = await client.query({
        query: SALES_ORDER_PICK_LIST,
        variables: { companyId, salesOrderId, startZoneId },
        fetchPolicy: 'network-only'
      });
      if (leavingRef.current) return;
      if (data?.company) setCompany(data.company);
      applyList(seq, data?.salesOrderPickList);
      setLoadError(null);
    } catch (e) {
      if (leavingRef.current) return;
      // A refusal the server wrote for the operator (the order is standard-workflow, gone, or not
      // this user's to read) means there is nothing to pick here — say why and go back. Anything
      // else is a transport failure: full-page error on the first load, silence on a background
      // refresh (the list on screen is still the last good answer).
      const message = operatorFacingMessage(e, '');
      if (message) {
        leave(
          classifyPickRefusal(message) === 'lost'
            ? 'Not in picking'
            : 'Cannot pick this order',
          message
        );
        return;
      }
      console.error('salesOrderPickList failed:', e);
      setLoadError((current) => current ?? e);
    }
  }, [applyList, client, companyId, leave, salesOrderId, startZoneId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  /**
   * What a refusal means for the whole screen (see classifyPickRefusal). Returns the result the
   * caller acts on: handled refusals have already been shown / navigated.
   */
  const handleRefusal = useCallback(
    (error: unknown, fallback: string = ACTION_FALLBACK): PickActionResult => {
      const message = operatorFacingMessage(error, '');
      switch (classifyPickRefusal(message)) {
        case 'lost':
          // Stop scanning and go back: the order was cancelled, held or split under the picker.
          leave('Not in picking', message || PICK_VERIFY_NOT_PICKING_MESSAGE);
          return { ok: false, handled: true, message };
        case 'notThisOrder':
          leave('Standard workflow', message);
          return { ok: false, handled: true, message };
        case 'pickComplete':
          if (!leavingRef.current) {
            leavingRef.current = true;
            alert('Pick complete', message, 5, 'info');
            navigate(salesOrderVerifyPath(salesOrderId), { replace: true });
          }
          return { ok: false, handled: true, message };
        case 'claim':
          // This handheld no longer holds the claim. Re-read: the list then shows who does (or,
          // when the claim was simply cleared, takes it again).
          alert('Pick claim', message, 8, 'warning');
          autoStartArmedRef.current = true;
          loadList();
          return { ok: false, handled: true, message };
        default:
          return { ok: false, handled: false, message: message || fallback, reason: refusalReason(error) };
      }
    },
    [leave, loadList, navigate, salesOrderId]
  );

  const perform = useCallback(
    async (
      busyKind: PickBusy,
      run: () => Promise<PickList | null | undefined>,
      fallback?: string
    ): Promise<PickActionResult> => {
      if (leavingRef.current) return { ok: false, handled: true, message: '' };
      const seq = ++requestSeqRef.current;
      if (busyKind) setBusy(busyKind);
      try {
        const next = await run();
        applyList(seq, next);
        return { ok: true, list: next ?? null };
      } catch (e) {
        const result = handleRefusal(e, fallback);
        // A refusal about the line (wrong zone, already picked) or a lost answer means the list on
        // screen may no longer be the server's: every write recomputes the FEFO stop and reads the
        // line fresh, and a confirm whose response was dropped may have committed. Nothing else
        // re-reads while this picker holds the claim, so re-read now — newest-request-wins drops it
        // if a later write answers first, and a moved zone / a line now picked reaches the sheet.
        if (!result.ok && !result.handled) void loadList();
        return result;
      } finally {
        if (busyKind) setBusy(null);
      }
    },
    [applyList, handleRefusal, loadList]
  );

  const start = useCallback(
    async (takeOver: boolean) => {
      // Any start — automatic, Take over, Try again — is this sighting's attempt: its answer (a
      // list claimed by me) must not trigger a second, automatic one.
      autoStartArmedRef.current = false;
      setStartError(null);
      const result = await perform(
        'start',
        async () => {
          const { data } = await startMutation({
            variables: {
              companyId,
              salesOrderId,
              takeOver: takeOver || null,
              startZoneId
            }
          });
          return data?.salesOrderPickStart;
        },
        'Could not start Pick on this order. Please try again.'
      );
      if (!result.ok && !result.handled) setStartError(result.message);
    },
    [companyId, perform, salesOrderId, startMutation, startZoneId]
  );

  // The route guard and the claim, re-evaluated on every answer — the load and each mutation's list.
  useEffect(() => {
    if (!list || leavingRef.current) return;
    const guard = pickListGuard(list);
    if (guard.kind === 'verify') {
      leavingRef.current = true;
      navigate(guard.path, { replace: true });
      return;
    }
    if (guard.kind === 'leave') {
      leave(guard.title, guard.message);
      return;
    }
    const claim = pickClaimView(list);
    if (claim.kind === 'other') {
      // Blocked by someone else: the panel says who. Re-arm, so the claim is taken the moment a
      // re-read finds it free (they released it).
      autoStartArmedRef.current = true;
      return;
    }
    if (pickShouldAutoStart(claim) && autoStartArmedRef.current) start(false);
  }, [leave, list, navigate, start]);

  const claim = list ? pickClaimView(list) : null;
  const blockedByLiveClaim = claim?.kind === 'other' && !claim.stale;
  useEffect(() => {
    if (!blockedByLiveClaim) return undefined;
    const timer = setInterval(() => {
      loadList();
    }, PICK_CLAIM_POLL_MS);
    return () => clearInterval(timer);
  }, [blockedByLiveClaim, loadList]);

  const confirmLine = useCallback(
    ({ orderItemId, zoneId, unit }: PickConfirmLineArgs) =>
      perform(
        null,
        async () => {
          const { data } = await confirmMutation({
            variables: {
              companyId,
              salesOrderId,
              input: { orderItemId, zoneId, unit },
              startZoneId
            }
          });
          return data?.salesOrderPickConfirmLine;
        },
        'Could not confirm this line. Please try again.'
      ),
    [companyId, confirmMutation, perform, salesOrderId, startZoneId]
  );

  // A unit-scan (controlled) line sends the units scanned as `foundQuantity` (the server refuses
  // any other number) and a `reason`, which it requires.
  const markShort = useCallback(
    (
      orderItemId: string,
      foundQuantity: number,
      { blocking = true, reason = null }: { blocking?: boolean; reason?: string | null } = {}
    ) =>
      perform(
        blocking ? 'short' : null,
        async () => {
          const { data } = reason
            ? await markShortWithReasonMutation({
                variables: { companyId, salesOrderId, orderItemId, foundQuantity, reason, startZoneId }
              })
            : await markShortMutation({
                variables: { companyId, salesOrderId, orderItemId, foundQuantity, startZoneId }
              });
          return data?.salesOrderPickMarkShort;
        },
        'Could not mark this line short. Please try again.'
      ),
    [companyId, markShortMutation, markShortWithReasonMutation, perform, salesOrderId, startZoneId]
  );

  // "Couldn't find them all here": not blocking — the line sheet holds its own in-flight state.
  const markStopShort = useCallback(
    (orderItemId: string, zoneId: string, foundQuantity: number) =>
      perform(
        null,
        async () => {
          const { data } = await markStopShortMutation({
            variables: {
              companyId,
              salesOrderId,
              orderItemId,
              zoneId,
              foundQuantity,
              startZoneId
            }
          });
          return data?.salesOrderPickMarkStopShort;
        },
        'Could not record what this zone had. Please try again.'
      ),
    [companyId, markStopShortMutation, perform, salesOrderId, startZoneId]
  );

  // Not blocking: the line sheet holds its own in-flight state, one scan at a time.
  const scanUnit = useCallback(
    ({ orderItemId, zoneId, unit }: PickScanUnitArgs) =>
      perform(
        null,
        async () => {
          const { data } = await scanUnitMutation({
            variables: { companyId, salesOrderId, orderItemId, zoneId: zoneId || null, unit, startZoneId }
          });
          return data?.salesOrderPickScanUnit;
        },
        'Could not record this unit. Please try again.'
      ),
    [companyId, perform, salesOrderId, scanUnitMutation, startZoneId]
  );

  const removeUnit = useCallback(
    (orderItemId: string, serialNumber?: string | null) =>
      perform(
        null,
        async () => {
          const { data } = await removeUnitMutation({
            variables: { companyId, salesOrderId, orderItemId, serialNumber: serialNumber || null, startZoneId }
          });
          return data?.salesOrderPickRemoveUnit;
        },
        'Could not remove this unit. Please try again.'
      ),
    [companyId, perform, removeUnitMutation, salesOrderId, startZoneId]
  );

  // With `zoneId`, only that confirmed zone of the line is undone; its other zones stay picked.
  const resetLine = useCallback(
    (orderItemId: string, zoneId?: string | null) =>
      perform(
        'reset',
        async () => {
          const { data } = await resetMutation({
            variables: {
              companyId,
              salesOrderId,
              orderItemId,
              zoneId: zoneId || null,
              startZoneId
            }
          });
          return data?.salesOrderPickResetLine;
        },
        zoneId
          ? 'Could not undo this zone. Please try again.'
          : 'Could not undo this line. Please try again.'
      ),
    [companyId, perform, resetMutation, salesOrderId, startZoneId]
  );

  // Not blocking: the line sheet holds its own in-flight state (and refuses a second action) while
  // a skip is answered, exactly as for a confirm.
  const skipZone = useCallback(
    (orderItemId: string, zoneId: string) =>
      perform(
        null,
        async () => {
          const { data } = await skipZoneMutation({
            variables: { companyId, salesOrderId, orderItemId, zoneId, startZoneId }
          });
          return data?.salesOrderPickSkipZone;
        },
        'Could not skip this zone. Please try again.'
      ),
    [companyId, perform, salesOrderId, skipZoneMutation, startZoneId]
  );

  const restoreZones = useCallback(
    (orderItemId: string) =>
      perform(
        'restore',
        async () => {
          const { data } = await restoreZonesMutation({
            variables: { companyId, salesOrderId, orderItemId, startZoneId }
          });
          return data?.salesOrderPickRestoreZones;
        },
        'Could not undo the skip. Please try again.'
      ),
    [companyId, perform, restoreZonesMutation, salesOrderId, startZoneId]
  );

  // Not blocking, like a skip: the line sheet holds its own in-flight state. The answer carries what
  // was quarantined as well as the list, so the sheet can say so (and, for Missing, where next).
  const quarantine = useCallback(
    async (
      run: () => Promise<(PickQuarantine & { list?: PickList | null }) | null | undefined>,
      fallback: string
    ): Promise<PickQuarantineResult> => {
      let answer: PickQuarantine | null = null;
      const result = await perform(
        null,
        async () => {
          const data = await run();
          if (!data) return null;
          const { list: next, ...rest } = data;
          answer = rest;
          return next;
        },
        fallback
      );
      return result.ok ? { ...result, quarantine: answer } : result;
    },
    [perform]
  );

  const quarantineMissing = useCallback(
    ({ orderItemId, zoneId, quarantineZoneId }: PickQuarantineMissingArgs) =>
      quarantine(async () => {
        const { data } = await quarantineMissingMutation({
          variables: {
            companyId,
            salesOrderId,
            input: { orderItemId, zoneId, quarantineZoneId },
            startZoneId
          }
        });
        return data?.salesOrderPickQuarantineMissing;
      }, 'Could not quarantine the missing stock. Please try again.'),
    [companyId, quarantine, quarantineMissingMutation, salesOrderId, startZoneId]
  );

  const quarantineDamaged = useCallback(
    ({ orderItemId, zoneId, unit, quantity }: PickQuarantineDamagedArgs) =>
      quarantine(async () => {
        const { data } = await quarantineDamagedMutation({
          variables: {
            companyId,
            salesOrderId,
            input: { orderItemId, zoneId, unit, quantity: quantity ?? null },
            startZoneId
          }
        });
        return data?.salesOrderPickQuarantineDamaged;
      }, 'Could not quarantine the damaged stock. Please try again.'),
    [companyId, quarantine, quarantineDamagedMutation, salesOrderId, startZoneId]
  );

  // COMBINED SHIPMENT (@/lib/combineShipment). Read once the list is here (the card shows a shipment
  // this order is already in); the ALERT — another order for the client still being picked — waits
  // for the end of the pick path: every line picked or short, before Complete Pick.
  const atEndOfPath = !!list && claim?.kind === 'mine' && list.openLineCount === 0 && !completed;
  const combine = useCombineStep({
    companyId,
    salesOrderId,
    enabled: !!list,
    armed: atEndOfPath && !busy
  });
  // Complete Pick tapped before the read answered (or with an alert not shown yet): show the alert
  // first. True = the alert is up and the complete waits for the picker.
  const beforeComplete = useCallback(async () => {
    const context = combine.loaded ? combine.context : await combine.refresh();
    if (!combine.unseenIn(context)) return false;
    combine.openSheet();
    return true;
  }, [combine]);

  const complete = useCallback(async () => {
    if (leavingRef.current) return;
    setBusy('complete');
    try {
      const { data } = await completeMutation({ variables: { companyId, salesOrderId } });
      const { verifyOrder, shortOrder } = data?.salesOrderPickComplete || {};
      leavingRef.current = true;
      const completion: PickCompletion = {
        verifyOrderId: verifyOrder?.id || salesOrderId,
        verifyOrderNumber: verifyOrder?.vendorSalesOrderNumber || null,
        shortOrderNumber: shortOrder?.vendorSalesOrderNumber || null
      };
      // With shorts the found units continue on a NEW order (original orders split) and the short
      // remainder is cancelled; the picker is told both numbers.
      if (shortOrder) alert('Pick complete', pickCompleteText(completion), 8, 'success');
      setCompleted(completion);
    } catch (e) {
      const result = handleRefusal(e, 'Could not complete Pick. Please try again.');
      if (!result.ok && !result.handled) {
        alert('Could not complete Pick', result.message, 8, 'error');
        // The server's gate disagreed with the list's `canComplete` (a line still open, no tote):
        // re-read, so the button and its blocked reason say what the server just said.
        void loadList();
      }
    } finally {
      setBusy(null);
    }
  }, [companyId, completeMutation, handleRefusal, loadList, salesOrderId]);

  const release = useCallback(async () => {
    const result = await perform(
      'release',
      async () => {
        await releaseMutation({ variables: { companyId, salesOrderId } });
        return null;
      },
      'Could not release this order. Please try again.'
    );
    if (result.ok) {
      leavingRef.current = true;
      navigate('/sales-orders');
    } else if (!result.handled) {
      alert('Could not release', result.message, 6, 'error');
    }
  }, [companyId, navigate, perform, releaseMutation, salesOrderId]);

  if (!list) {
    if (loadError) return <ApiError error={loadError} />;
    return <Spinner />;
  }
  // Leaving (or about to): never flash the list of an order this screen will not pick. Pick
  // complete is the exception — its panel covers the list whatever a late answer says about it.
  if (!completed && pickListGuard(list).kind !== 'stay') return <Spinner />;

  return (
    <SalesOrderPick
      list={list}
      company={company}
      companyId={companyId}
      setStartZoneId={setStartZoneId}
      busy={busy}
      startError={startError}
      onStart={start}
      onConfirmLine={confirmLine}
      onMarkShort={markShort}
      onMarkStopShort={markStopShort}
      onScanUnit={scanUnit}
      onRemoveUnit={removeUnit}
      onResetLine={resetLine}
      onSkipZone={skipZone}
      onRestoreZones={restoreZones}
      onQuarantineMissing={quarantineMissing}
      onQuarantineDamaged={quarantineDamaged}
      onComplete={complete}
      onRelease={release}
      completed={completed}
      combine={combine}
      combineAtEnd={atEndOfPath}
      onBeforeComplete={beforeComplete}
      // The verify order: the child holding the picked lines after a short split.
      onContinueToVerify={() =>
        completed &&
        navigate(salesOrderVerifyPath(completed.verifyOrderId), { replace: true })
      }
      onDone={() => navigate('/')}
    />
  );
}

export default SalesOrderPickData;
